import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from './ui/card';
import {
  Users,
  Calendar,
  DollarSign,
  TrendingUp,
  MessageCircle,
  CreditCard,
  Clock,
  MapPin,
  Globe,
  Target,
  Award,
  Activity,
  ArrowLeft,
  Share,
  Heart,
  Wallet,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Copy,
  Phone,
  Mail,
  Crown,
  Zap,
  Trash2,
  X
} from 'lucide-react';

const MemberGroupDetails = ({ group, user, onNavigate, onJoinChat, onMakePayment, onViewMember }) => {
  const [groupStats, setGroupStats] = useState({
    totalContributions: 0,
    myContributions: 0,
    nextPayout: null,
    myTurn: false,
    cycleProgress: 0,
    memberRank: 0
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [membersList, setMembersList] = useState([]);
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // États pour les boutons interactifs
  const [isJoiningChat, setIsJoiningChat] = useState(false);
  const [isMakingPayment, setIsMakingPayment] = useState(false);
  const [isProcessingCrypto, setIsProcessingCrypto] = useState(false);
  const [actionFeedback, setActionFeedback] = useState(null); // {type: 'success'|'error', message: '...'}
  const [chatAvailable, setChatAvailable] = useState(true); // À déterminer selon les données du groupe
  const [paymentAvailable, setPaymentAvailable] = useState(true); // À déterminer selon les données du groupe
  const [removingMemberId, setRemovingMemberId] = useState(null); // ID du membre en cours de suppression
  
  // Admin functionalities states
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fonctions améliorées pour les boutons interactifs
  const showFeedback = (type, message, duration = 3000) => {
    setActionFeedback({ type, message });
    setTimeout(() => setActionFeedback(null), duration);
  };

  // Composant de feedback visuel
  const FeedbackAlert = ({ feedback, onClose }) => {
    if (!feedback) return null;

    const isError = feedback.type === 'error';
    const bgColor = isError ? 'bg-red-500' : 'bg-green-500';
    const textColor = 'text-white';
    const Icon = isError ? AlertCircle : CheckCircle;

    return (
      <div className={`fixed top-4 right-4 z-50 ${bgColor} ${textColor} px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 max-w-md`}>
        <Icon className="w-5 h-5 flex-shrink-0" />
        <span className="flex-1 text-sm font-medium">{feedback.message}</span>
        <button 
          onClick={() => onClose()}
          className="text-white/80 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const handleJoinChat = async (groupId) => {
    console.log('[JOIN_CHAT] Attempting to join chat for group:', groupId);
    
    if (!chatAvailable) {
      showFeedback('error', 'Fonction indisponible : ce groupe n\'a pas encore activé la messagerie');
      return;
    }

    setIsJoiningChat(true);
    
    try {
      // Vérifier les droits d'accès au chat
      if (!user) {
        throw new Error('Vous devez être connecté pour rejoindre le chat');
      }

      // Vérifier si l'utilisateur est membre du groupe
      const isMember = group.members?.some(member => member.id === user.id) || group.admin_id === user.id;
      if (!isMember) {
        throw new Error('Vous devez être membre du groupe pour accéder au chat');
      }

      // Vérifier l'état du réseau
      if (!navigator.onLine) {
        throw new Error('Impossible de rejoindre le chat : vérifiez votre connexion internet');
      }

      // Appeler la fonction de navigation vers le chat
      if (onJoinChat) {
        await onJoinChat(groupId);
        showFeedback('success', 'Chat ouvert avec succès !');
      } else {
        throw new Error('Fonction de chat non disponible pour le moment');
      }
    } catch (error) {
      console.error('[JOIN_CHAT] Error:', error);
      showFeedback('error', `Impossible de rejoindre le chat : ${error.message}`);
    } finally {
      setIsJoiningChat(false);
    }
  };

  const handleMakePayment = async (groupId) => {
    console.log('[MAKE_PAYMENT] Attempting payment for group:', groupId);
    
    if (!paymentAvailable) {
      showFeedback('error', 'Fonction indisponible : ce groupe n\'a pas encore activé le paiement');
      return;
    }

    setIsMakingPayment(true);
    
    try {
      // Vérifications préalables
      if (!user) {
        throw new Error('Vous devez être connecté pour effectuer un paiement');
      }

      const isMember = group.members?.some(member => member.id === user.id) || group.admin_id === user.id;
      console.log('[MAKE_PAYMENT] Membership check:', {
        userId: user.id,
        adminId: group.admin_id,
        isAdmin: group.admin_id === user.id,
        membersIds: group.members?.map(m => m.id),
        isMemberRegular: group.members?.some(member => member.id === user.id),
        isMemberFinal: isMember
      });
      
      if (!isMember) {
        throw new Error('Seuls les membres du groupe peuvent effectuer des paiements');
      }

      // Vérifier s'il y a un montant à payer
      if (!group.contribution_amount || group.contribution_amount <= 0) {
        throw new Error('Aucun montant de contribution défini pour ce groupe');
      }

      // Vérifier l'état du groupe
      if (group.status === 'closed' || group.status === 'completed') {
        throw new Error('Ce groupe est fermé et n\'accepte plus de paiements');
      }

      // Appeler la fonction de paiement
      if (onMakePayment) {
        await onMakePayment(groupId);
        showFeedback('success', 'Interface de paiement ouverte !');
      } else {
        throw new Error('Interface de paiement non disponible pour le moment');
      }
    } catch (error) {
      console.error('[MAKE_PAYMENT] Error:', error);
      showFeedback('error', `Impossible d'effectuer le paiement : ${error.message}`);
    } finally {
      setIsMakingPayment(false);
    }
  };

  const handleCryptoPayment = async (groupId) => {
    console.log('[CRYPTO_PAYMENT] Attempting crypto payment for group:', groupId);
    
    setIsProcessingCrypto(true);
    
    try {
      // Vérifications préalables
      if (!user) {
        throw new Error('Vous devez être connecté pour effectuer un paiement crypto');
      }

      const isMember = group.members?.some(member => member.id === user.id) || group.admin_id === user.id;
      console.log('[CRYPTO_PAYMENT] Membership check:', {
        userId: user.id,
        adminId: group.admin_id,
        isAdmin: group.admin_id === user.id,
        isMemberFinal: isMember
      });
      
      if (!isMember) {
        throw new Error('Seuls les membres du groupe peuvent effectuer des paiements');
      }

      // Vérifier la disponibilité du service crypto
      const cryptoAvailable = await checkCryptoService();
      if (!cryptoAvailable) {
        throw new Error('Service de cryptomonnaie temporairement indisponible');
      }

      // Lancer le flux crypto
      if (onNavigate) {
        await onNavigate('crypto-pay', { groupId });
        showFeedback('success', 'Flux de paiement crypto lancé !');
      } else {
        throw new Error('Navigation vers le paiement crypto non disponible');
      }
    } catch (error) {
      console.error('[CRYPTO_PAYMENT] Error:', error);
      showFeedback('error', `Impossible d'effectuer le paiement crypto : ${error.message}`);
    } finally {
      setIsProcessingCrypto(false);
    }
  };

  const checkCryptoService = async () => {
    try {
      // Simulation d'un appel API pour vérifier le service crypto
      // Dans la vraie implémentation, faire un appel vers /api/crypto/status
      const response = await fetch('/api/crypto/wallets', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.ok;
    } catch (error) {
      console.error('[CRYPTO_SERVICE] Check failed:', error);
      return false;
    }
  };

  const handleCallAdmin = async () => {
    console.log('[CALL_ADMIN] Attempting to call admin for group:', group.name);
    
    try {
      // Vérifier si l'utilisateur est connecté
      if (!user) {
        throw new Error('Vous devez être connecté pour appeler l\'administrateur');
      }

      // Récupérer les informations de l'admin
      let adminPhoneNumber = null;
      let adminName = 'l\'administrateur';

      if (group.admin_id) {
        // Si on a l'admin dans les membres du groupe
        const adminMember = group.members?.find(member => member.id === group.admin_id);
        if (adminMember && adminMember.phone) {
          adminPhoneNumber = adminMember.phone;
          adminName = adminMember.username || adminMember.name || 'l\'administrateur';
        }
        
        // Si pas trouvé dans les membres, utiliser les infos directes du groupe
        if (!adminPhoneNumber && group.admin_phone) {
          adminPhoneNumber = group.admin_phone;
          adminName = group.admin_name || 'l\'administrateur';
        }
      }

      console.log('[CALL_ADMIN] Admin phone lookup:', {
        adminId: group.admin_id,
        adminPhoneNumber: adminPhoneNumber ? '***' + adminPhoneNumber.slice(-4) : 'not found',
        adminName
      });

      // Vérifier si on a un numéro
      if (!adminPhoneNumber) {
        throw new Error('Aucun numéro de téléphone enregistré pour cet administrateur');
      }

      // Nettoyer et formater le numéro
      let cleanNumber = adminPhoneNumber.replace(/[^\d+]/g, '');
      if (!cleanNumber.startsWith('+')) {
        // Ajouter le préfixe Kenya si pas de code pays
        cleanNumber = '+254' + cleanNumber.replace(/^0/, '');
      }

      console.log('[CALL_ADMIN] Formatted phone number:', cleanNumber);

      // Vérifier la compatibilité de l'appareil
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobile = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isDesktop = !isMobile;

      // Détecter si on est sur web/desktop sans capacité d'appel
      if (isDesktop && !navigator.userAgent.includes('Mobile')) {
        console.log('[CALL_ADMIN] Desktop/web environment detected');
        showFeedback('error', `Fonction indisponible sur cet appareil. Numéro de ${adminName}: ${cleanNumber}`);
        return;
      }

      // Créer le lien tel:
      const telLink = `tel:${cleanNumber}`;
      console.log('[CALL_ADMIN] Attempting to launch phone app with:', telLink);

      // Créer un élément temporaire pour déclencher l'appel
      const tempLink = document.createElement('a');
      tempLink.href = telLink;
      tempLink.style.display = 'none';
      document.body.appendChild(tempLink);

      // Feedback visuel immédiat
      showFeedback('success', `Lancement de l'appel vers ${adminName}...`, 3000);

      // Déclencher l'appel
      try {
        tempLink.click();
        console.log('[CALL_ADMIN] Phone app launch attempted successfully');
        
        // Vibration légère si disponible
        if (navigator.vibrate) {
          navigator.vibrate(100);
        }
        
      } catch (launchError) {
        console.error('[CALL_ADMIN] Phone app launch failed:', launchError);
        
        // Fallback : ouvrir dans une nouvelle fenêtre/tab
        window.open(telLink, '_blank');
      }

      // Nettoyer l'élément temporaire
      setTimeout(() => {
        document.body.removeChild(tempLink);
      }, 1000);

    } catch (error) {
      console.error('[CALL_ADMIN] Error:', error);
      showFeedback('error', error.message);
      
      // Log détaillé pour debug
      console.error('[CALL_ADMIN] Detailed error:', {
        message: error.message,
        groupId: group.id,
        groupName: group.name,
        adminId: group.admin_id,
        timestamp: new Date().toISOString()
      });
    }
  };

  useEffect(() => {
    if (group) {
      calculateGroupStats();
      loadRecentActivity();
      loadMembersList();
      checkGroupFeatures();
    }
  }, [group]);

  const checkGroupFeatures = () => {
    if (!group) return;

    // Déterminer la disponibilité du chat
    const isChatAvailable = group.chat_enabled !== false && 
                           group.status !== 'closed' && 
                           group.status !== 'suspended';
    setChatAvailable(isChatAvailable);

    // Déterminer la disponibilité des paiements (more permissive)
    const isPaymentAvailable = group.payment_enabled !== false && 
                              group.status !== 'closed' &&
                              group.status !== 'suspended' &&
                              group.status !== 'completed' &&
                              group.contribution_amount > 0;
    setPaymentAvailable(isPaymentAvailable);

    console.log('[GROUP_FEATURES] Group status:', group.status);
    console.log('[GROUP_FEATURES] Contribution amount:', group.contribution_amount);
    console.log('[GROUP_FEATURES] Payment enabled:', group.payment_enabled);
    console.log('[GROUP_FEATURES] Chat available:', isChatAvailable, 'Payment available:', isPaymentAvailable);
  };

  const copyGroupCode = async () => {
    console.log('[COPY_CODE] Attempting to copy group invitation code');
    
    try {
      const invitationCode = group.permanent_invitation_code || 'ABC123';
      
      if (!navigator.clipboard) {
        throw new Error('Fonction de copie non disponible dans ce navigateur');
      }

      await navigator.clipboard.writeText(invitationCode);
      showFeedback('success', `Code d'invitation copié : ${invitationCode}`);
      
    } catch (error) {
      console.error('[COPY_CODE] Error:', error);
      
      // Fallback: try selecting text manually
      try {
        const textArea = document.createElement('textarea');
        textArea.value = group.permanent_invitation_code || 'ABC123';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        
        showFeedback('success', 'Code d\'invitation copié !');
      } catch (fallbackError) {
        console.error('[COPY_CODE] Fallback failed:', fallbackError);
        showFeedback('error', 'Impossible de copier le code. Sélectionnez-le manuellement.');
      }
    }
  };

  const calculateGroupStats = () => {
    if (!group) return;

    // Mock calculations - replace with actual data
    const totalMembers = group.members?.length || 0;
    const totalContributions = group.contribution_amount * totalMembers * 3; // Assuming 3 rounds
    const myContributions = group.contribution_amount * 3;
    const cycleProgress = (group.current_cycle || 1) / (totalMembers || 1) * 100;
    const memberRank = Math.floor(Math.random() * totalMembers) + 1;

    setGroupStats({
      totalContributions,
      myContributions,
      nextPayout: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
      myTurn: memberRank === (group.current_beneficiary_index || 0) + 1,
      cycleProgress,
      memberRank
    });
  };

  // Check if current user is admin of this group
  const isAdmin = user && group && (user.id === group.admin_id || user.role === 'admin');

  // Function to handle group deletion
  const handleDeleteGroup = async () => {
    if (!isAdmin) return;
    
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups/${group.id}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('Groupe supprimé avec succès. Les membres ont été notifiés.');
        setShowDeleteGroupModal(false);
        onNavigate('groups'); // Redirect to groups list
      } else {
        const error = await response.json();
        alert(`Erreur lors de la suppression: ${error.detail || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      alert('Erreur lors de la suppression du groupe. Veuillez réessayer.');
    }
    setIsDeleting(false);
  };

  // Function to handle member removal
  const handleRemoveMember = async () => {
    if (!isAdmin || !memberToRemove) return;
    
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups/${group.id}/members/${memberToRemove.id}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert(`${memberToRemove.username} a été exclu du groupe et notifié par email.`);
        setShowRemoveMemberModal(false);
        setMemberToRemove(null);
        loadMembersList(); // Refresh members list
      } else {
        const error = await response.json();
        alert(`Erreur lors de l'exclusion: ${error.detail || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Erreur lors de l\'exclusion du membre. Veuillez réessayer.');
    }
    setIsDeleting(false);
  };

  const loadRecentActivity = () => {
    // Mock recent activity data
    setRecentActivity([
      {
        id: 1,
        type: 'contribution',
        member: 'John Doe',
        action: 'made a contribution',
        amount: group.contribution_amount,
        time: '2 hours ago',
        icon: CreditCard,
        color: 'text-green-400'
      },
      {
        id: 2,
        type: 'payout',
        member: 'Jane Smith',
        action: 'received payout',
        amount: group.contribution_amount * (group.members?.length || 1),
        time: '1 day ago',
        icon: DollarSign,
        color: 'text-blue-400'
      },
      {
        id: 3,
        type: 'join',
        member: 'Mike Johnson',
        action: 'joined the group',
        time: '3 days ago',
        icon: Users,
        color: 'text-purple-400'
      },
      {
        id: 4,
        type: 'chat',
        member: 'Sarah Wilson',
        action: 'sent a message in chat',
        time: '5 hours ago',
        icon: MessageCircle,
        color: 'text-orange-400'
      }
    ]);
  };

  const loadMembersList = () => {
    if (!group.members) return;

    // Mock member data - replace with actual member fetch
    const mockMembers = group.members.map((memberId, index) => ({
      id: memberId,
      username: `Member ${index + 1}`,
      email: `member${index + 1}@example.com`,
      phone: `+254700${String(index + 1).padStart(6, '0')}`,
      role: memberId === group.admin_id ? 'admin' : 'member',
      joinedAt: new Date(Date.now() - (index * 7 * 24 * 60 * 60 * 1000)),
      contributionsCount: Math.floor(Math.random() * 5) + 1,
      lastActive: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000),
      isActive: Math.random() > 0.2,
      totalContributed: group.contribution_amount * (Math.floor(Math.random() * 5) + 1)
    }));

    setMembersList(mockMembers);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: group?.currency || 'KES'
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(new Date(date));
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - new Date(date)) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const isUserAdmin = user?.id === group?.admin_id;
  const isUserMember = group?.members?.includes(user?.id);

  if (!group) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4" />
          <p>Group not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Feedback Alert */}
      <FeedbackAlert feedback={actionFeedback} onClose={() => setActionFeedback(null)} />
      
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => onNavigate('groups')}
              className="p-2 bg-white/10 rounded-full backdrop-blur-sm hover:bg-white/20 transition-all"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-white">{group.name}</h1>
              <p className="text-slate-300">{group.description}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Admin-only buttons */}
            {isAdmin && (
              <button
                onClick={() => setShowDeleteGroupModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                title="Supprimer le groupe"
              >
                <Trash2 className="w-4 h-4" />
                <span>Supprimer Groupe</span>
              </button>
            )}
            
            <button
              onClick={() => {
                // Check if we can safely use share API
                const canShare = navigator.share && 
                  window === window.top && // Not in iframe
                  window.isSecureContext; // Only in secure context
                
                if (canShare) {
                  navigator.share({ 
                    title: group.name, 
                    text: group.description 
                  }).catch((err) => {
                    console.log('Share failed:', err);
                    // Fallback: copy to clipboard
                    navigator.clipboard?.writeText(`${group.name}: ${group.description}`)
                      .then(() => alert('Informations du groupe copiées !'))
                      .catch(() => alert('Impossible de partager ou copier'));
                  });
                } else {
                  // Fallback: copy to clipboard
                  navigator.clipboard?.writeText(`${group.name}: ${group.description}`)
                    .then(() => alert('Informations du groupe copiées !'))
                    .catch(() => alert('Impossible de copier'));
                }
              }}
              className="p-2 bg-white/10 rounded-full backdrop-blur-sm hover:bg-white/20 transition-all"
            >
              <Share className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={() => handleJoinChat(group.id)}
              disabled={isJoiningChat || !chatAvailable}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                !chatAvailable 
                  ? 'bg-gray-500 text-gray-300 cursor-not-allowed' 
                  : isJoiningChat 
                    ? 'bg-blue-400 text-white cursor-wait' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {isJoiningChat ? (
                <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              ) : (
                <MessageCircle className="w-4 h-4" />
              )}
              <span>{isJoiningChat ? 'Connexion...' : 'Join Chat'}</span>
            </button>
          </div>
        </div>

        {/* Group Hero Card */}
        <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white mb-8">
          <CardContent className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Group Avatar & Basic Info */}
              <div className="text-center lg:text-left">
                <div className="w-24 h-24 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto lg:mx-0 mb-4">
                  {group.name.substring(0, 2).toUpperCase()}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{group.name}</h3>
                <div className="flex items-center justify-center lg:justify-start space-x-2 mb-2">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-300">{group.region || 'Global'}</span>
                </div>
                <div className="flex items-center justify-center lg:justify-start space-x-2 mb-4">
                  <Globe className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-300">{group.language === 'sw' ? 'Kiswahili' : group.language === 'en' ? 'English' : 'Français'}</span>
                </div>
                <div className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-green-500/20 text-green-300">
                  <Activity className="w-3 h-3 mr-1" />
                  {group.is_active ? 'Active' : 'Inactive'}
                </div>
              </div>

              {/* Key Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{group.members?.length || 0}</div>
                  <div className="text-sm text-slate-400">Members</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{formatCurrency(group.contribution_amount)}</div>
                  <div className="text-sm text-slate-400">Contribution</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{group.current_cycle || 1}</div>
                  <div className="text-sm text-slate-400">Current Cycle</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-white">{group.payout_frequency}</div>
                  <div className="text-sm text-slate-400">Frequency</div>
                </div>
              </div>

              {/* My Status */}
              <div className="bg-white/5 rounded-lg p-4">
                <h4 className="text-lg font-medium text-white mb-4">My Status</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-300">My Contributions:</span>
                    <span className="text-white font-medium">{formatCurrency(groupStats.myContributions)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-300">Position in Queue:</span>
                    <span className="text-white font-medium">#{groupStats.memberRank}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-300">Next Turn:</span>
                    {groupStats.myTurn ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-300">
                        <Crown className="w-3 h-3 mr-1" />
                        Your Turn!
                      </span>
                    ) : (
                      <span className="text-white font-medium">{formatDate(groupStats.nextPayout)}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex space-x-1 mb-8 bg-white/10 rounded-lg p-1">
          {['overview', 'members', 'activity', 'payments'].map((tab) => (
            <button
              key={tab}
              onClick={() => {
                console.log('[TAB_SWITCH] Switching to tab:', tab);
                try {
                  setActiveTab(tab);
                  const tabNames = {
                    overview: 'Vue d\'ensemble',
                    members: 'Membres',
                    activity: 'Activité',
                    payments: 'Paiements'
                  };
                  showFeedback('success', `${tabNames[tab]} affiché`, 1500);
                } catch (error) {
                  console.error('[TAB_SWITCH] Error:', error);
                  showFeedback('error', 'Erreur lors du changement d\'onglet');
                }
              }}
              className={`flex-1 px-4 py-2 rounded-md transition-all capitalize ${
                activeTab === tab 
                  ? 'bg-white/20 text-white' 
                  : 'text-slate-300 hover:text-white hover:bg-white/10'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Group Description */}
                <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
                  <CardHeader>
                    <CardTitle>About This Group</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-300 mb-4">{group.description}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-slate-400">Theme:</span>
                        <p className="text-white">{group.theme || 'General'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-slate-400">Started:</span>
                        <p className="text-white">{formatDate(group.start_date)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Contribution Rules */}
                <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
                  <CardHeader>
                    <CardTitle>Contribution Rules</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <DollarSign className="w-5 h-5 text-green-400" />
                        <div>
                          <p className="text-white">Monthly contribution of {formatCurrency(group.contribution_amount)}</p>
                          <p className="text-sm text-slate-400">Due on the 1st of each month</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Clock className="w-5 h-5 text-blue-400" />
                        <div>
                          <p className="text-white">Payout every {group.payout_frequency}</p>
                          <p className="text-sm text-slate-400">Rotating order based on joining date</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Wallet className="w-5 h-5 text-purple-400" />
                        <div>
                          <p className="text-white">Crypto payments accepted</p>
                          <p className="text-sm text-slate-400">USDT, USDC supported</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'members' && (
              <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Members ({membersList.length})</span>
                    <button
                      onClick={() => {
                        console.log('[TOGGLE_MEMBERS] Toggling member list display');
                        try {
                          setShowAllMembers(!showAllMembers);
                          showFeedback('success', showAllMembers ? 'Liste réduite' : 'Tous les membres affichés', 1500);
                        } catch (error) {
                          console.error('[TOGGLE_MEMBERS] Error:', error);
                          showFeedback('error', 'Erreur lors de l\'affichage des membres');
                        }
                      }}
                      className="text-sm text-blue-400 hover:text-blue-300 transition-colors flex items-center space-x-1"
                    >
                      <span>{showAllMembers ? 'Voir moins' : 'Voir tous'}</span>
                      <ChevronRight className={`w-3 h-3 transition-transform ${showAllMembers ? 'rotate-90' : ''}`} />
                    </button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {(showAllMembers ? membersList : membersList.slice(0, 5)).map((member) => (
                      <div key={member.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-all">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold">
                              {member.username.substring(0, 2).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <p className="font-medium text-white">{member.username}</p>
                              {member.role === 'admin' && (
                                <Crown className="w-4 h-4 text-yellow-400" />
                              )}
                            </div>
                            <p className="text-sm text-slate-400">
                              Joined {formatDate(member.joinedAt)} • {member.contributionsCount} contributions
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                              member.isActive ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-300'
                            }`}>
                              {member.isActive ? 'Active' : 'Inactive'}
                            </div>
                          </div>
                          
                          {/* Admin-only remove member button */}
                          {isAdmin && member.id !== user.id && member.role !== 'admin' && (
                            <button
                              onClick={() => {
                                console.log('[REMOVE_MEMBER] Initiating member removal for:', member.username);
                                try {
                                  if (removingMemberId === member.id) {
                                    showFeedback('error', 'Exclusion déjà en cours...');
                                    return;
                                  }
                                  setMemberToRemove(member);
                                  setShowRemoveMemberModal(true);
                                  showFeedback('success', `Préparation de l'exclusion de ${member.username}`, 2000);
                                } catch (error) {
                                  console.error('[REMOVE_MEMBER] Error:', error);
                                  showFeedback('error', 'Erreur lors de la préparation de l\'exclusion');
                                }
                              }}
                              disabled={removingMemberId === member.id}
                              className={`flex items-center space-x-1 px-3 py-1 rounded-md transition-colors text-sm ${
                                removingMemberId === member.id 
                                  ? 'bg-gray-500/20 text-gray-400 cursor-not-allowed' 
                                  : 'bg-red-600/20 hover:bg-red-600/40 text-red-400 hover:text-red-300'
                              }`}
                              title={`Exclure ${member.username}`}
                            >
                              {removingMemberId === member.id ? (
                                <div className="w-3 h-3 animate-spin rounded-full border border-gray-400 border-t-transparent"></div>
                              ) : (
                                <Trash2 className="w-3 h-3" />
                              )}
                              <span>{removingMemberId === member.id ? 'En cours...' : 'Exclure'}</span>
                            </button>
                          )}
                        </div>
                        <p className="text-sm text-slate-400 mt-1">
                          {formatTimeAgo(member.lastActive)}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'activity' && (
              <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
                <CardHeader>
                  <CardTitle>Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {recentActivity.map((activity) => {
                      const IconComponent = activity.icon;
                      return (
                        <div key={activity.id} className="flex items-center space-x-4 p-4 bg-white/5 rounded-lg">
                          <div className="p-2 bg-slate-700 rounded-full">
                            <IconComponent className={`w-5 h-5 ${activity.color}`} />
                          </div>
                          <div className="flex-1">
                            <p className="text-white">
                              <span className="font-medium">{activity.member}</span> {activity.action}
                              {activity.amount && (
                                <span className="font-medium"> {formatCurrency(activity.amount)}</span>
                              )}
                            </p>
                            <p className="text-sm text-slate-400">{activity.time}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'payments' && (
              <div className="space-y-6">
                {/* Payment Status */}
                <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
                  <CardHeader>
                    <CardTitle>My Payment Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-6 h-6 text-green-400" />
                        <div>
                          <p className="font-medium text-white">Payment Current</p>
                          <p className="text-sm text-green-300">Last payment: {formatCurrency(group.contribution_amount)} on Jan 1, 2025</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleMakePayment(group.id)}
                        disabled={isMakingPayment || !paymentAvailable}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                          !paymentAvailable 
                            ? 'bg-gray-500 text-gray-300 cursor-not-allowed' 
                            : isMakingPayment 
                              ? 'bg-green-400 text-white cursor-wait' 
                              : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                      >
                        {isMakingPayment ? (
                          <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        ) : (
                          <CreditCard className="w-4 h-4" />
                        )}
                        <span>{isMakingPayment ? 'Ouverture...' : 'Make Payment'}</span>
                      </button>
                    </div>
                  </CardContent>
                </Card>

                {/* Payment History */}
                <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
                  <CardHeader>
                    <CardTitle>Payment History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[1, 2, 3, 4, 5].map((payment) => (
                        <div key={payment} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                            <div>
                              <p className="text-white">Payment #{payment}</p>
                              <p className="text-sm text-slate-400">Dec {payment}, 2024</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-medium">{formatCurrency(group.contribution_amount)}</p>
                            <p className="text-sm text-green-300">Confirmed</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Actions */}
            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <button
                  onClick={() => handleMakePayment(group.id)}
                  disabled={isMakingPayment || !paymentAvailable}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all ${
                    !paymentAvailable 
                      ? 'bg-gray-600/20 text-gray-400 cursor-not-allowed' 
                      : isMakingPayment 
                        ? 'bg-green-400/30 text-green-200 cursor-wait' 
                        : 'bg-green-600/20 hover:bg-green-600/30 text-green-300'
                  }`}
                >
                  {isMakingPayment ? (
                    <div className="w-5 h-5 animate-spin rounded-full border-2 border-green-300 border-t-transparent"></div>
                  ) : (
                    <CreditCard className="w-5 h-5" />
                  )}
                  <span>{isMakingPayment ? 'Ouverture...' : 'Make Payment'}</span>
                  {!isMakingPayment && <ChevronRight className="w-4 h-4 ml-auto" />}
                </button>
                
                <button
                  onClick={() => handleCryptoPayment(group.id)}
                  disabled={isProcessingCrypto}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all ${
                    isProcessingCrypto 
                      ? 'bg-purple-400/30 text-purple-200 cursor-wait' 
                      : 'bg-purple-600/20 hover:bg-purple-600/30 text-purple-300'
                  }`}
                >
                  {isProcessingCrypto ? (
                    <div className="w-5 h-5 animate-spin rounded-full border-2 border-purple-300 border-t-transparent"></div>
                  ) : (
                    <Wallet className="w-5 h-5" />
                  )}
                  <span>{isProcessingCrypto ? 'Lancement...' : 'Pay with Crypto'}</span>
                  {!isProcessingCrypto && <ChevronRight className="w-4 h-4 ml-auto" />}
                </button>
                
                <button
                  onClick={() => handleJoinChat(group.id)}
                  disabled={isJoiningChat || !chatAvailable}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all ${
                    !chatAvailable 
                      ? 'bg-gray-600/20 text-gray-400 cursor-not-allowed' 
                      : isJoiningChat 
                        ? 'bg-blue-400/30 text-blue-200 cursor-wait' 
                        : 'bg-blue-600/20 hover:bg-blue-600/30 text-blue-300'
                  }`}
                >
                  {isJoiningChat ? (
                    <div className="w-5 h-5 animate-spin rounded-full border-2 border-blue-300 border-t-transparent"></div>
                  ) : (
                    <MessageCircle className="w-5 h-5" />
                  )}
                  <span>{isJoiningChat ? 'Connexion...' : 'Join Chat'}</span>
                  {!isJoiningChat && <ChevronRight className="w-4 h-4 ml-auto" />}
                </button>
                
                <button
                  onClick={handleCallAdmin}
                  className="w-full flex items-center space-x-3 p-3 bg-orange-600/20 hover:bg-orange-600/30 text-orange-300 rounded-lg transition-all"
                >
                  <Phone className="w-5 h-5" />
                  <span>Call Admin</span>
                  <ChevronRight className="w-4 h-4 ml-auto" />
                </button>
              </CardContent>
            </Card>

            {/* Group Admin Contact */}
            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardHeader>
                <CardTitle>Group Admin</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Crown className="w-8 h-8 text-white" />
                  </div>
                  <p className="font-medium text-white mb-2">Group Admin</p>
                  <p className="text-sm text-slate-400 mb-4">Manages group activities</p>
                  
                  <div className="space-y-2">
                    <button className="w-full flex items-center justify-center space-x-2 p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-all">
                      <Mail className="w-4 h-4" />
                      <span>Send Message</span>
                    </button>
                    <button 
                      onClick={handleCallAdmin}
                      className="w-full flex items-center justify-center space-x-2 p-2 text-green-400 hover:bg-green-500/20 rounded-lg transition-all"
                    >
                      <Phone className="w-4 h-4" />
                      <span>Call Admin</span>
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Invite Friends */}
            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardHeader>
                <CardTitle>Invite Friends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center">
                  <p className="text-slate-300 mb-4">Share this group with friends</p>
                  <div className="flex items-center space-x-2 p-3 bg-white/5 rounded-lg mb-4">
                    <code className="flex-1 text-sm text-blue-300">{group.permanent_invitation_code || 'ABC123'}</code>
                    <button
                      onClick={copyGroupCode}
                      className="p-2 text-white hover:bg-white/10 rounded transition-all"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <button
                    onClick={() => {
                      // Check if we can safely use share API
                      const canShare = navigator.share && 
                        window === window.top && // Not in iframe
                        window.isSecureContext; // Only in secure context
                      
                      const shareText = `Join ${group.name} with code: ${group.permanent_invitation_code}`;
                      
                      if (canShare) {
                        navigator.share({ 
                          title: group.name, 
                          text: shareText 
                        }).catch((err) => {
                          console.log('Share failed:', err);
                          // Fallback: copy to clipboard
                          navigator.clipboard?.writeText(shareText)
                            .then(() => alert('Code d\'invitation copié !'))
                            .catch(() => alert('Impossible de partager ou copier'));
                        });
                      } else {
                        // Fallback: copy to clipboard
                        navigator.clipboard?.writeText(shareText)
                          .then(() => alert('Code d\'invitation copié !'))
                          .catch(() => alert('Impossible de copier'));
                      }
                    }}
                    className="w-full flex items-center justify-center space-x-2 p-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                  >
                    <Share className="w-4 h-4" />
                    <span>Share Group</span>
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      {/* Delete Group Confirmation Modal */}
      {showDeleteGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="flex justify-between items-center p-4 border-b">
              <button 
                onClick={() => setShowDeleteGroupModal(false)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 px-3 py-2 rounded-md hover:bg-gray-100"
                aria-label="Retour"
              >
                <ArrowLeft size={20} />
                <span>Retour</span>
              </button>
              <h2 className="text-xl font-semibold text-red-600">Supprimer le Groupe</h2>
              <button 
                onClick={() => setShowDeleteGroupModal(false)}
                className="text-gray-500 hover:text-gray-700 p-2"
                aria-label="Fermer"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-4">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Confirmer la suppression
                </h3>
                <p className="text-gray-600 mb-4">
                  Êtes-vous sûr de vouloir supprimer le groupe <strong>"{group.name}"</strong> ?
                </p>
                <div className="bg-red-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-red-700">
                    <strong>Attention :</strong> Cette action est irréversible et supprimera :
                  </p>
                  <ul className="text-sm text-red-700 mt-2 text-left space-y-1">
                    <li>• Tous les membres du groupe</li>
                    <li>• L'historique des cotisations</li>
                    <li>• Les messages et discussions</li>
                    <li>• Toutes les données associées</li>
                  </ul>
                  <p className="text-sm text-red-700 mt-2">
                    Tous les membres seront notifiés par email de cette suppression.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    console.log('[MODAL_CANCEL] Cancelling group deletion');
                    setShowDeleteGroupModal(false);
                    showFeedback('success', 'Suppression annulée', 2000);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    console.log('[DELETE_GROUP] Initiating group deletion');
                    handleDeleteGroup();
                  }}
                  disabled={isDeleting}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isDeleting ? (
                    <span className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      <span>Suppression...</span>
                    </span>
                  ) : (
                    'Supprimer Définitivement'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Member Confirmation Modal */}
      {showRemoveMemberModal && memberToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="flex justify-between items-center p-4 border-b">
              <button 
                onClick={() => {
                  setShowRemoveMemberModal(false);
                  setMemberToRemove(null);
                }}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 px-3 py-2 rounded-md hover:bg-gray-100"
                aria-label="Retour"
              >
                <ArrowLeft size={20} />
                <span>Retour</span>
              </button>
              <h2 className="text-xl font-semibold text-red-600">Exclure un Membre</h2>
              <button 
                onClick={() => {
                  setShowRemoveMemberModal(false);
                  setMemberToRemove(null);
                }}
                className="text-gray-500 hover:text-gray-700 p-2"
                aria-label="Fermer"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-4">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Confirmer l'exclusion
                </h3>
                <p className="text-gray-600 mb-4">
                  Êtes-vous sûr de vouloir exclure <strong>{memberToRemove.username}</strong> du groupe ?
                </p>
                <div className="bg-orange-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-orange-700">
                    <strong>Cette action :</strong>
                  </p>
                  <ul className="text-sm text-orange-700 mt-2 text-left space-y-1">
                    <li>• Retirera ce membre du groupe immédiatement</li>
                    <li>• Lui enverra une notification par email</li>
                    <li>• Supprimera son accès aux discussions</li>
                    <li>• Peut être annulée en invitant à nouveau le membre</li>
                  </ul>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    console.log('[MODAL_CANCEL] Cancelling member removal');
                    setShowRemoveMemberModal(false);
                    setMemberToRemove(null);
                    setRemovingMemberId(null);
                    showFeedback('success', 'Exclusion annulée', 2000);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    console.log('[REMOVE_MEMBER] Executing member removal');
                    if (memberToRemove) {
                      setRemovingMemberId(memberToRemove.id);
                    }
                    handleRemoveMember();
                  }}
                  disabled={isDeleting}
                  className="flex-1 bg-orange-600 text-white py-2 px-4 rounded hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isDeleting ? (
                    <span className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      <span>Exclusion...</span>
                    </span>
                  ) : (
                    'Exclure le Membre'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberGroupDetails;